import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import logoBlackSmall from '@/assets/images/logo-black-small.png'
import logoScoder from '@/assets/images/clients/logo-scoder.png'
import FlowSvg from './flow-svg'
import AgentsSvg from './agents-svg'
import Seo from '@/components/seo/seo'
import { breadcrumbSchema, caseStudySchema } from '@/components/seo/json-ld'
import { resolveLang } from '@/lib/site'

interface IntegrationItem {
  icon: string
  name: string
  role: string
  tag: string
  tagVariant?: 'gov' | 'llm'
}

interface CostItem {
  label: string
  detail: string
  value: string
}

interface MetricCard {
  label: string
  value: string
  unit?: string
  delta: string
  accent: 'primary' | 'success' | 'warning' | 'info'
  smallValue?: boolean
}

interface BreakdownItem {
  role: string
  hours: string
  rate: string
  total: string
}

export default function CaseScoder() {
  const { t, i18n } = useTranslation()
  const lang = resolveLang(i18n.resolvedLanguage)
  const currentLang = i18n.resolvedLanguage === 'pt-BR' ? 'PT' : 'EN'
  const toggleLang = () => {
    i18n.changeLanguage(i18n.resolvedLanguage === 'pt-BR' ? 'en' : 'pt-BR')
  }

  const integrationItems = t('caseScoder.integrations.items', {
    returnObjects: true,
  }) as IntegrationItem[]
  const costItems = t('caseScoder.costs.items', { returnObjects: true }) as CostItem[]
  const metricCards = t('caseScoder.metrics.cards', { returnObjects: true }) as MetricCard[]
  const breakdownItems = t('caseScoder.metrics.breakdownItems', {
    returnObjects: true,
  }) as BreakdownItem[]

  return (
    <div className="theme-light min-h-screen bg-white md:bg-[#cbd5e0] pt-11 md:pt-12">
      <Seo
        title={t('seo.caseScoder.title') as string}
        description={t('seo.caseScoder.description') as string}
        path="/case-scoder"
        type="article"
        jsonLd={[
          breadcrumbSchema(lang, [
            { name: 'Bravic', path: '/' },
            { name: 'Cases', path: '/#cases' },
            { name: 'Scoder', path: '/case-scoder' },
          ]),
          caseStudySchema(lang, {
            name: t('seo.caseScoder.title') as string,
            description: t('seo.caseScoder.description') as string,
            path: '/case-scoder',
            client: 'Scoder',
          }),
        ]}
      />
      {/* Fixed top bar */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-[#1a1a2e] border-b-2 border-primary">
        <div className="w-full md:w-[210mm] mx-auto px-5 md:px-12 py-2.5 flex justify-between items-center">
          <span className="font-display text-sm font-black tracking-tighter">
            <span className="text-white/90">BRAVIC</span>
            <span className="text-primary">/</span>
          </span>
          <button
            onClick={toggleLang}
            className="flex items-center gap-1.5 text-white/50 hover:text-primary transition-colors cursor-pointer"
          >
            <span className="material-symbols-outlined text-base">language</span>
            <span className="font-display text-[11px] font-semibold tracking-wider">
              {currentLang}
            </span>
          </button>
        </div>
      </div>

      {/* Page 1 — Cover */}
      <DocumentPage>
        <div className="relative bg-[#0f0f1a] flex flex-col justify-between flex-1 overflow-hidden">
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              backgroundImage:
                'linear-gradient(rgba(26,188,156,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(26,188,156,0.04) 1px, transparent 1px)',
              backgroundSize: '40px 40px',
            }}
          />
          <div className="absolute -top-24 -right-24 w-[500px] h-[500px] rounded-full bg-[radial-gradient(circle,rgba(0,166,147,0.18)_0%,transparent_70%)] pointer-events-none" />
          <div className="absolute -bottom-16 -left-20 w-[300px] h-[300px] rounded-full bg-[radial-gradient(circle,rgba(0,166,147,0.08)_0%,transparent_70%)] pointer-events-none" />
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary via-primary-fixed to-transparent" />

          <div className="relative z-10 flex-1 flex flex-col justify-center px-6 md:px-14 py-12 md:py-16">
            <div className="inline-block bg-primary/15 text-primary text-[11px] font-semibold uppercase tracking-[2px] px-3.5 py-1.5 rounded-full mb-6 w-fit">
              {t('caseScoder.cover.badge')}
            </div>
            <h1 className="font-display text-3xl md:text-[38px] font-extrabold text-white leading-[1.1] tracking-tight mb-3">
              {t('caseScoder.cover.title')}{' '}
              <span className="text-primary">{t('caseScoder.cover.titleAccent')}</span>
            </h1>
            <p className="text-[15px] text-white/60 leading-relaxed max-w-[640px]">
              {t('caseScoder.cover.description')}{' '}
              <strong className="text-white font-semibold">
                {t('caseScoder.cover.descriptionHighlight')}
              </strong>
              .
            </p>
          </div>

          {/* Client band */}
          <div className="relative z-10 border-t border-white/10 px-6 md:px-14 py-4 md:py-5 flex items-center gap-5">
            <div className="bg-[#1B2B3A] rounded-xl p-2.5 flex items-center justify-center shrink-0">
              <img src={logoScoder} alt="Scoder" className="h-8 md:h-10 w-auto object-contain" />
            </div>
            <div>
              <p className="text-[10px] font-semibold text-white/40 uppercase tracking-widest mb-0.5">
                {t('caseScoder.cover.clientLabel')}
              </p>
              <p className="text-sm font-bold text-white">{t('caseScoder.cover.clientName')}</p>
              <p className="text-xs text-white/50">{t('caseScoder.cover.clientTagline')}</p>
            </div>
            <a
              href="https://www.scoder.com.br"
              target="_blank"
              rel="noopener noreferrer"
              className="ml-auto text-xs text-primary hover:text-primary-fixed transition-colors hidden md:block"
            >
              www.scoder.com.br
            </a>
          </div>
          <div className="h-1 bg-gradient-to-r from-primary to-primary-fixed" />
        </div>
      </DocumentPage>

      {/* Page 2 — Fluxograma Operacional */}
      <DocumentPage>
        <DocumentHeader label={t('caseScoder.docLabel')} />
        <div className="px-5 md:px-12 py-6 md:py-10 flex-1">
          <SectionLabel>{t('caseScoder.flowchart.sectionLabel')}</SectionLabel>
          <SectionTitle title={t('caseScoder.flowchart.title')} />
          <p className="text-sm text-on-surface-variant leading-relaxed mb-6 max-w-[680px]">
            {t('caseScoder.flowchart.subtitle')}
          </p>

          {/* Legend */}
          <div className="flex flex-wrap gap-4 md:gap-6 mb-6 text-xs text-on-surface-variant font-medium">
            <div className="flex items-center gap-2.5">
              <span className="w-7 h-[18px] rounded border-2 border-primary bg-white" />
              {t('caseScoder.flowchart.legendProcess')}
            </div>
            <div className="flex items-center gap-2.5">
              <span className="w-4 h-4 rounded-sm border-2 border-[#F2994A] bg-gradient-to-b from-white to-[#FEF3E6] rotate-45" />
              {t('caseScoder.flowchart.legendDecision')}
            </div>
            <div className="flex items-center gap-2.5">
              <span className="w-[18px] h-[18px] rounded-full border-2 border-dashed border-[#EB5757] bg-white" />
              {t('caseScoder.flowchart.legendLoop')}
            </div>
            <div className="flex items-center gap-2.5">
              <span className="font-mono text-[11px] font-bold text-[#27AE60]">— SIM</span>
              {t('caseScoder.flowchart.legendYes')}
            </div>
            <div className="flex items-center gap-2.5">
              <span className="font-mono text-[11px] font-bold text-[#EB5757]">— NÃO</span>
              {t('caseScoder.flowchart.legendNo')}
            </div>
          </div>

          {/* Flowchart SVG */}
          <div className="bg-white overflow-x-auto">
            <FlowSvg />
          </div>
        </div>
        <PageFooter page={2} label={t('caseScoder.footer.page')} />
      </DocumentPage>

      {/* Page 3 — Hierarquia de Agentes */}
      <DocumentPage>
        <DocumentHeader label={t('caseScoder.docLabel')} />
        <div className="px-5 md:px-12 py-6 md:py-10 flex-1">
          <SectionLabel>{t('caseScoder.agents.sectionLabel')}</SectionLabel>
          <SectionTitle title={t('caseScoder.agents.title')} />
          <p className="text-sm text-on-surface-variant leading-relaxed mb-8 max-w-[680px]">
            {t('caseScoder.agents.subtitle')}
          </p>

          <div className="bg-white overflow-x-auto pb-6">
            <AgentsSvg />
          </div>

          {/* Legend */}
          <div className="flex flex-wrap gap-6 mt-6 text-xs font-medium text-on-surface-variant">
            <div className="flex items-center gap-2.5">
              <span className="w-5 h-5 rounded bg-[#1B2B3A]" />
              {t('caseScoder.agents.legendOrchestrator')}
            </div>
            <div className="flex items-center gap-2.5">
              <span className="w-5 h-5 rounded bg-primary-container" />
              {t('caseScoder.agents.legendDirectors')}
            </div>
            <div className="flex items-center gap-2.5">
              <span className="w-5 h-5 rounded bg-white border-2 border-primary-container" />
              {t('caseScoder.agents.legendSubAgents')}
            </div>
          </div>
        </div>
        <PageFooter page={3} label={t('caseScoder.footer.page')} />
      </DocumentPage>

      {/* Page 4 — Integrações */}
      <DocumentPage>
        <DocumentHeader label={t('caseScoder.docLabel')} />
        <div className="px-5 md:px-12 py-6 md:py-10 flex-1">
          <SectionLabel>{t('caseScoder.integrations.sectionLabel')}</SectionLabel>
          <SectionTitle title={t('caseScoder.integrations.title')} />
          <p className="text-sm text-on-surface-variant leading-relaxed mb-8 max-w-[680px]">
            {t('caseScoder.integrations.subtitle')}
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {integrationItems.map((item, i) => (
              <IntegrationCard key={i} {...item} />
            ))}
          </div>
        </div>
        <PageFooter page={4} label={t('caseScoder.footer.page')} />
      </DocumentPage>

      {/* Page 5 — Custos + ROI */}
      <DocumentPage>
        <DocumentHeader label={t('caseScoder.docLabel')} />
        <div className="px-5 md:px-12 py-6 md:py-10 flex-1">
          <SectionLabel>{t('caseScoder.costs.sectionLabel')}</SectionLabel>
          <SectionTitle title={t('caseScoder.costs.title')} />
          <p className="text-sm text-on-surface-variant leading-relaxed mb-8 max-w-[680px]">
            {t('caseScoder.costs.subtitle')}
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Cost block */}
            <div className="bg-[#f4f6f8] border border-[#e2e8f0] rounded-lg overflow-hidden">
              <div className="px-5 py-4 border-b border-[#e2e8f0] flex items-center justify-between">
                <h3 className="text-sm font-bold text-[#1a1a2e]">
                  {t('caseScoder.costs.costHeader')}
                </h3>
                <span className="text-[10px] font-semibold bg-primary/10 text-primary-container px-2.5 py-1 rounded-full">
                  {t('caseScoder.costs.costPill')}
                </span>
              </div>
              <div className="divide-y divide-[#e2e8f0]">
                {costItems.map((item, i) => (
                  <CostRow key={i} {...item} />
                ))}
              </div>
              <div className="px-5 py-3 bg-[#1a1a2e] flex items-center justify-between">
                <span className="text-xs font-semibold text-white/60">
                  {t('caseScoder.costs.costTotalLabel')}
                </span>
                <span className="text-sm font-bold text-primary">
                  {t('caseScoder.costs.costTotalValue')}
                </span>
              </div>
            </div>

            {/* ROI card */}
            <div className="bg-[#1a1a2e] rounded-lg p-5 md:p-6 relative overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-primary via-primary-fixed to-transparent" />
              <p className="text-[10px] font-semibold text-white/40 uppercase tracking-widest mb-1">
                {t('caseScoder.costs.roiLabel')}
              </p>
              <h3 className="font-display text-lg font-bold text-white mb-5">
                {t('caseScoder.costs.roiTitle')}
              </h3>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-white/50">{t('caseScoder.costs.roiGross')}</span>
                  <span className="text-sm font-bold text-emerald-400">
                    {t('caseScoder.costs.roiGrossValue')}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-white/50">{t('caseScoder.costs.roiCost')}</span>
                  <span className="text-sm font-bold text-red-400">
                    {t('caseScoder.costs.roiCostValue')}
                  </span>
                </div>
                <div className="border-t border-white/10 pt-3 flex items-center justify-between">
                  <span className="text-xs font-semibold text-white/70">
                    {t('caseScoder.costs.roiNetLabel')}
                  </span>
                  <span className="text-xl font-extrabold text-primary">
                    {t('caseScoder.costs.roiNetValue')}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-white/50">{t('caseScoder.costs.roiPercent')}</span>
                  <span className="text-sm font-bold text-emerald-400">
                    {t('caseScoder.costs.roiPercentValue')}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-white/50">{t('caseScoder.costs.roiAnnual')}</span>
                  <span className="text-sm font-bold text-emerald-400">
                    {t('caseScoder.costs.roiAnnualValue')}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <PageFooter page={5} label={t('caseScoder.footer.page')} />
      </DocumentPage>

      {/* Page 6 — Métricas de Impacto */}
      <DocumentPage>
        <DocumentHeader label={t('caseScoder.docLabel')} />
        <div className="px-5 md:px-12 py-6 md:py-10 flex-1">
          <SectionLabel>{t('caseScoder.metrics.sectionLabel')}</SectionLabel>
          <SectionTitle title={t('caseScoder.metrics.title')} />
          <p className="text-sm text-on-surface-variant leading-relaxed mb-8 max-w-[680px]">
            {t('caseScoder.metrics.subtitle')}
          </p>

          {/* Metrics grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
            {metricCards.map((card, i) => (
              <MetricCardComponent key={i} {...card} />
            ))}
          </div>

          {/* Breakdown table */}
          <div className="bg-[#f4f6f8] border border-[#e2e8f0] rounded-lg overflow-hidden">
            <div className="px-5 py-4 border-b border-[#e2e8f0] flex items-center justify-between flex-wrap gap-2">
              <h3 className="text-sm font-bold text-[#1a1a2e]">
                {t('caseScoder.metrics.breakdownTitle')}
              </h3>
              <span className="text-[10px] font-semibold bg-primary/10 text-primary-container px-2.5 py-1 rounded-full">
                {t('caseScoder.metrics.breakdownPill')}
              </span>
            </div>
            <div className="divide-y divide-[#e2e8f0]">
              {breakdownItems.map((item, i) => (
                <BreakdownRow
                  key={i}
                  {...item}
                  hoursLabel={t('caseScoder.metrics.breakdownHoursLabel')}
                  rateLabel={t('caseScoder.metrics.breakdownRateLabel')}
                />
              ))}
            </div>
            <div className="px-5 py-3 bg-[#1a1a2e] flex items-center justify-between">
              <span className="text-xs font-semibold text-white/60">
                {t('caseScoder.metrics.breakdownFooter')}
              </span>
              <span className="text-sm font-bold text-primary">
                {t('caseScoder.metrics.breakdownFooterValue')}
              </span>
            </div>
          </div>

          {/* Footer brand */}
          <div className="mt-10 flex items-center gap-4 pt-6 border-t border-[#e2e8f0]">
            <img src={logoBlackSmall} alt="Bravic" className="h-6" />
            <div>
              <p className="text-[10px] text-on-surface-variant uppercase tracking-widest">
                {t('caseScoder.footer.creditLabel')}
              </p>
              <p className="text-xs font-semibold text-[#1a1a2e]">
                {t('caseScoder.footer.creditText')}
              </p>
            </div>
            <span className="ml-auto text-[10px] font-mono text-on-surface-variant">
              {t('caseScoder.footer.reportVersion')}
            </span>
          </div>
        </div>
        <PageFooter page={6} label={t('caseScoder.footer.page')} />
      </DocumentPage>

      <div className="h-8" />
    </div>
  )
}

function DocumentPage({ children }: { children: ReactNode }) {
  return (
    <section className="relative bg-white md:shadow-[0_8px_40px_rgba(0,0,0,0.15)] w-full md:w-[210mm] md:min-h-[297mm] mx-auto md:my-8 overflow-hidden flex flex-col border-b border-[#e2e8f0] md:border-none md:rounded-sm">
      {children}
    </section>
  )
}

function DocumentHeader({ label }: { label: string }) {
  return (
    <div className="bg-[#1a1a2e] px-5 md:px-12 py-2.5 md:py-3 flex justify-between items-center border-b-2 border-primary">
      <span className="text-[9px] md:text-[10px] font-bold text-white/70 tracking-widest uppercase">
        {label}
      </span>
      <span className="font-display text-xs md:text-sm font-black tracking-tighter">
        <span className="text-white/90">BRAVIC</span>
        <span className="text-primary">/</span>
      </span>
    </div>
  )
}

function PageFooter({ page, label }: { page: number; label: string }) {
  return (
    <div className="px-5 md:px-12 py-2.5 md:py-3 border-t border-[#e2e8f0] flex justify-between items-center text-[9px] text-[#718096]">
      <img src={logoBlackSmall} alt="Bravic" className="h-4" />
      <span>
        {label} {page}
      </span>
    </div>
  )
}

function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <span className="inline-block text-[11px] font-semibold uppercase tracking-[2px] text-primary-container mb-2">
      {children}
    </span>
  )
}

function SectionTitle({ title }: { title: string }) {
  return (
    <div className="mb-4">
      <div className="w-10 md:w-12 h-1 bg-primary rounded-full mb-3" />
      <h2 className="font-display text-xl md:text-[28px] font-bold tracking-tight text-[#1a1a2e]">
        {title}
      </h2>
    </div>
  )
}

function IntegrationCard({
  icon,
  name,
  role,
  tag,
  tagVariant,
}: {
  icon: string
  name: string
  role: string
  tag: string
  tagVariant?: string
}) {
  const tagColors =
    tagVariant === 'gov'
      ? 'bg-amber-100 text-amber-700'
      : tagVariant === 'llm'
        ? 'bg-purple-100 text-purple-700'
        : 'bg-primary/10 text-primary-container'

  return (
    <div className="bg-white border border-[#e2e8f0] rounded-lg p-4 flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-[#f4f6f8] flex items-center justify-center shrink-0">
          <span className="material-symbols-outlined text-primary-container text-lg">{icon}</span>
        </div>
        <h4 className="text-sm font-bold text-[#1a1a2e]">{name}</h4>
      </div>
      <p className="text-[11px] text-on-surface-variant leading-relaxed flex-1">{role}</p>
      <span
        className={`text-[9px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full w-fit ${tagColors}`}
      >
        {tag}
      </span>
    </div>
  )
}

function CostRow({ label, detail, value }: { label: string; detail: string; value: string }) {
  return (
    <div className="px-5 py-3 flex items-center justify-between gap-4">
      <div className="min-w-0">
        <p className="text-xs font-semibold text-[#1a1a2e]">{label}</p>
        <p className="text-[10px] text-on-surface-variant">{detail}</p>
      </div>
      <span className="text-sm font-bold text-[#1a1a2e] shrink-0">{value}</span>
    </div>
  )
}

function MetricCardComponent({
  label,
  value,
  unit,
  delta,
  accent,
  smallValue,
}: {
  label: string
  value: string
  unit?: string
  delta: string
  accent: 'primary' | 'success' | 'warning' | 'info'
  smallValue?: boolean
}) {
  const accentColors = {
    primary: 'border-l-primary',
    success: 'border-l-emerald-500',
    warning: 'border-l-amber-500',
    info: 'border-l-sky-500',
  }
  const valueColors = {
    primary: 'text-[#1a1a2e]',
    success: 'text-emerald-600',
    warning: 'text-amber-600',
    info: 'text-[#1a1a2e]',
  }

  return (
    <div
      className={`bg-white border border-[#e2e8f0] rounded-lg p-4 border-l-4 ${accentColors[accent]}`}
    >
      <p className="text-[10px] font-semibold uppercase tracking-[1.5px] text-on-surface-variant mb-3">
        {label}
      </p>
      <div
        className={`font-display font-extrabold tracking-tight leading-none mb-2 ${valueColors[accent]} ${smallValue ? 'text-base leading-snug' : 'text-2xl md:text-[32px]'}`}
      >
        {value}
        {unit && <span className="text-sm font-semibold text-on-surface-variant ml-1">{unit}</span>}
      </div>
      <p className="text-[11px] text-on-surface-variant">{delta}</p>
    </div>
  )
}

function BreakdownRow({
  role,
  hours,
  rate,
  total,
  hoursLabel,
  rateLabel,
}: {
  role: string
  hours: string
  rate: string
  total: string
  hoursLabel: string
  rateLabel: string
}) {
  return (
    <div className="px-5 py-3 flex items-center justify-between gap-3">
      <span className="text-xs font-semibold text-[#1a1a2e] min-w-0">{role}</span>
      <div className="flex items-center gap-4 shrink-0">
        <div className="text-right">
          <span className="text-[9px] text-on-surface-variant block">{hoursLabel}</span>
          <span className="text-xs font-semibold text-[#1a1a2e]">{hours}</span>
        </div>
        <div className="text-right">
          <span className="text-[9px] text-on-surface-variant block">{rateLabel}</span>
          <span className="text-xs font-semibold text-[#1a1a2e]">{rate}</span>
        </div>
        <span className="text-sm font-bold text-primary-container w-24 text-right">{total}</span>
      </div>
    </div>
  )
}
